// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Production Orders Page
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Plus, Search, Edit, Trash2, Play, CheckCircle, XCircle, Clock, Package, ChevronRight, ClipboardList } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface StatChipProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  index: number;
}

function StatChip({ label, value, icon, color, index }: StatChipProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="group"
    >
      <Card className="p-4 transition-shadow duration-200 ease-standard hover:shadow-surface-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground truncate">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
          </div>
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-standard group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${color}29, ${color}0D)`,
              boxShadow: `0 0 0 1px ${color}26`,
              color,
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

interface ProductionOrder {
  _id: string;
  orderNumber: string;
  batchNumber: string;
  formulaId: string;
  formulaVersion: string;
  plantId: string;
  targetProductId: string;
  targetQty: number;
  targetUnit: string;
  sourceTankId: string;
  targetTanks: any[];
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string;
  actualEnd: string;
  status: string;
  costingMethod: string;
  jointCostMethod: string;
  createdBy: string;
  createdAt: string;
}

export function ProductionOrdersPage() {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<ProductionOrder | null>(null);
  const [formData, setFormData] = useState<any>({});
  const { get, post, put, del } = useApi();

  // Master data for the Formula/Plant/Target Product/Source Tank pickers —
  // these fields used to be raw ObjectId text inputs, which made the create
  // form unusable for anyone who didn't already know an id by heart.
  const [formulas, setFormulas] = useState<any[]>([]);
  const [plants, setPlants] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [tanks, setTanks] = useState<any[]>([]);
  const [allTanks, setAllTanks] = useState<any[]>([]);

  // Reserve Materials dialog — the backend has supported a formula-driven
  // reservation step (POST /orders/:id/reserve) all along, but no UI ever
  // called it, so released orders had no way to move to material_reserved.
  const [reservingOrder, setReservingOrder] = useState<ProductionOrder | null>(null);
  const [reserveLines, setReserveLines] = useState<any[]>([]);
  const [reserveTanks, setReserveTanks] = useState<any[]>([]);
  const [reserveSaving, setReserveSaving] = useState(false);

  useEffect(() => {
    fetchOrders();
    get('/api/manufacturing/boms').then((res: any) => setFormulas(res.formulas || [])).catch(() => {});
    get('/api/manufacturing/master/plants').then((res: any) => setPlants(res.plants || [])).catch(() => {});
    get('/api/manufacturing/products').then((res: any) => setProducts(res.products || [])).catch(() => {});
    get('/api/manufacturing/master/tanks').then((res: any) => setAllTanks(res.tanks || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get]);

  // Tanks are scoped to the chosen plant, so re-fetch whenever it changes.
  useEffect(() => {
    if (!formData.plantId) { setTanks([]); return; }
    get(`/api/manufacturing/master/tanks?plantId=${formData.plantId}`).then((res: any) => setTanks(res.tanks || [])).catch(() => setTanks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plantId]);

  async function fetchOrders() {
    setLoading(true);
    try {
      const res = await get('/api/manufacturing/orders');
      setOrders(res.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }

  async function openReserve(order: ProductionOrder) {
    setReservingOrder(order);
    setReserveLines([]);
    setReserveTanks([]);
    try {
      const [formulaRes, tanksRes] = await Promise.all([
        order.formulaId ? get(`/api/manufacturing/boms/${order.formulaId}`) : Promise.resolve(null),
        order.plantId ? get(`/api/manufacturing/master/tanks?plantId=${order.plantId}`) : Promise.resolve({ tanks: [] }),
      ]);
      setReserveTanks(tanksRes?.tanks || []);
      const formula = formulaRes?.formula;
      if (!formula) return;
      // Scale each BOM line from the formula's reference batch size to this
      // order's actual targetQty, so the reservation matches what's really
      // being produced rather than the formula's nominal batch.
      const scale = (order.targetQty || 0) / (formula.primaryProductQty || 1);
      setReserveLines(
        (formula.inputs || []).map((input: any) => ({
          productId: input.productId,
          productName: input.productName,
          unit: input.unit || 'kg',
          reservedQty: Math.round(input.qty * scale * 1000) / 1000,
          tankId: '',
        }))
      );
    } catch (error) {
      console.error('Failed to load formula for reservation:', error);
    }
  }

  async function handleReserveSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reservingOrder) return;
    const reservations = reserveLines
      .filter((l) => l.tankId && l.reservedQty > 0)
      .map((l) => ({ productId: l.productId, tankId: l.tankId, reservedQty: l.reservedQty, unit: l.unit }));
    if (!reservations.length) {
      alert('Pick a source tank for at least one ingredient line.');
      return;
    }
    setReserveSaving(true);
    try {
      await post(`/api/manufacturing/orders/${reservingOrder._id}/reserve`, { reservations });
      setReservingOrder(null);
      fetchOrders();
    } catch (error: any) {
      alert(error?.message || 'Failed to reserve materials.');
    } finally {
      setReserveSaving(false);
    }
  }
  
  function handleOpenCreate() {
    setEditingOrder(null);
    setFormData({
      orderNumber: '',
      formulaId: '',
      formulaVersion: '1.0',
      plantId: '',
      targetProductId: '',
      targetQty: 0,
      targetUnit: 'kg',
      sourceTankId: '',
      targetTanks: [],
      scheduledStart: new Date().toISOString().split('T')[0],
      scheduledEnd: new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0],
      status: 'draft',
      costingMethod: 'standard',
      jointCostMethod: 'nrv',
    });
    setShowDialog(true);
  }
  
  function handleEdit(order: ProductionOrder) {
    setEditingOrder(order);
    setFormData({ ...order });
    setShowDialog(true);
  }
  
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingOrder) {
        await put(`/api/manufacturing/orders/${editingOrder._id}`, formData);
      } else {
        await post('/api/manufacturing/orders', formData);
      }
      setShowDialog(false);
      fetchOrders();
    } catch (error) {
      console.error('Failed to save order:', error);
    }
  }
  
  async function handleAction(id: string, action: 'release' | 'start' | 'complete' | 'close' | 'cancel') {
    if (confirm(`Are you sure you want to ${action} this order?`)) {
      try {
        await post(`/api/manufacturing/orders/${id}/${action}`, {});
        fetchOrders();
      } catch (error) {
        console.error(`Failed to ${action} order:`, error);
      }
    }
  }
  
  async function handleDelete(id: string) {
    if (confirm('Are you sure you want to delete this order?')) {
      await del(`/api/manufacturing/orders/${id}`);
      fetchOrders();
    }
  }
  
  const filteredOrders = orders.filter(o =>
    o.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
    o.batchNumber.toLowerCase().includes(search.toLowerCase())
  ).filter(o => !filterStatus || o.status === filterStatus);

  // Real counts derived from the already-fetched orders list — no extra API calls.
  const inProgressCount = orders.filter(o => o.status === 'in_progress').length;
  const releasedCount = orders.filter(o => o.status === 'released' || o.status === 'material_reserved').length;
  const completedCount = orders.filter(o => o.status === 'completed' || o.status === 'closed').length;

  const statusConfig: Record<string, { icon: any; color: string; label: string }> = {
    draft: { icon: Clock, color: 'gray', label: 'Draft' },
    released: { icon: Play, color: 'blue', label: 'Released' },
    material_reserved: { icon: Package, color: 'purple', label: 'Material Reserved' },
    in_progress: { icon: Play, color: 'yellow', label: 'In Progress' },
    completed: { icon: CheckCircle, color: 'green', label: 'Completed' },
    closed: { icon: CheckCircle, color: 'gray', label: 'Closed' },
    cancelled: { icon: XCircle, color: 'red', label: 'Cancelled' },
    on_hold: { icon: Clock, color: 'orange', label: 'On Hold' },
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Production Orders</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Plan & release work orders, reserve raw crude, issue materials to WIP</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Order
        </Button>
      </div>

      {!loading && orders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatChip index={0} label="Total Orders" value={orders.length} icon={<ClipboardList className="w-5 h-5" />} color="#6E62FF" />
          <StatChip index={1} label="Released / Reserved" value={releasedCount} icon={<Play className="w-5 h-5" />} color="#3b82f6" />
          <StatChip index={2} label="In Progress" value={inProgressCount} icon={<Package className="w-5 h-5" />} color="#f59e0b" />
          <StatChip index={3} label="Completed" value={completedCount} icon={<CheckCircle className="w-5 h-5" />} color="#22c55e" />
        </div>
      )}

      <div className="flex gap-4 mb-4">
        <Input placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" leftIcon={<Search className="w-4 h-4" />} />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="released">Released</SelectItem>
            <SelectItem value="material_reserved">Material Reserved</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {loading ? (
        <Card className="p-8 text-center">Loading...</Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Batch #</TableHead>
                <TableHead>Formula</TableHead>
                <TableHead>Target Product</TableHead>
                <TableHead>Target Qty</TableHead>
                <TableHead>Source Tank</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Costing</TableHead>
                <TableHead className="w-56">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map(order => {
                const statusCfg = statusConfig[order.status] || { icon: Clock, color: 'gray', label: order.status };
                return (
                  <TableRow key={order._id}>
                    <TableCell className="font-mono font-medium">{order.orderNumber}</TableCell>
                    <TableCell className="font-mono text-sm">{order.batchNumber || '-'}</TableCell>
                    <TableCell>{formulas.find(f => f._id === order.formulaId)?.code || order.formulaId || '-'}</TableCell>
                    <TableCell>{products.find(p => p._id === order.targetProductId)?.name || order.targetProductId || '-'}</TableCell>
                    <TableCell>{(order.targetQty || 0).toLocaleString()} {order.targetUnit}</TableCell>
                    <TableCell className="font-mono text-sm">{allTanks.find(t => t._id === order.sourceTankId)?.code || order.sourceTankId || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {order.scheduledStart ? new Date(order.scheduledStart).toLocaleDateString() : 'Not scheduled'}
                      {order.scheduledEnd && <span className="text-gray-400 mx-1">→</span>}
                      {order.scheduledEnd && new Date(order.scheduledEnd).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={order.status === 'active' || order.status === 'in_progress' ? 'success' : order.status === 'draft' ? 'secondary' : order.status === 'cancelled' ? 'destructive' : 'outline'}>
                        {statusCfg.icon && <statusCfg.icon className="w-3 h-3 mr-1" />}
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{order.costingMethod}</Badge>
                      <Badge variant="secondary" className="ml-1">{order.jointCostMethod?.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(order)} title="Edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {order.status === 'draft' && (
                          <Button variant="ghost" size="icon" onClick={() => handleAction(order._id, 'release')} title="Release">
                            <Play className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {order.status === 'released' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openReserve(order)} title="Reserve Materials">
                              <Package className="w-4 h-4 text-purple-500" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleAction(order._id, 'start')} title="Start">
                              <Play className="w-4 h-4 text-green-500" />
                            </Button>
                          </>
                        )}
                        {order.status === 'material_reserved' && (
                          <Button variant="ghost" size="icon" onClick={() => handleAction(order._id, 'start')} title="Start">
                            <Play className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        {order.status === 'in_progress' && (
                          <Button variant="ghost" size="icon" onClick={() => handleAction(order._id, 'complete')} title="Complete">
                            <CheckCircle className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {['completed', 'closed'].includes(order.status) && (
                          <Button variant="ghost" size="icon" onClick={() => window.location.href = `/manufacturing/batch-ops/${order._id}`} title="View Batch">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(order._id)} title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-gray-500">No production orders found. Click "Create Order" to get started.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Edit Production Order' : 'Create Production Order'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Order Number *</label>
                  <Input value={formData.orderNumber} onChange={e => setFormData({...formData, orderNumber: e.target.value.toUpperCase()})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Formula *</label>
                  <Select value={formData.formulaId} onValueChange={v => setFormData({...formData, formulaId: v})}>
                    <SelectTrigger><SelectValue placeholder="Select formula…" /></SelectTrigger>
                    <SelectContent>
                      {formulas.map(f => (
                        <SelectItem key={f._id} value={f._id}>{f.code} v{f.version} — {f.primaryProductName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Formula Version</label>
                  <Input value={formData.formulaVersion} onChange={e => setFormData({...formData, formulaVersion: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plant *</label>
                  <Select value={formData.plantId} onValueChange={v => setFormData({...formData, plantId: v, sourceTankId: ''})}>
                    <SelectTrigger><SelectValue placeholder="Select plant…" /></SelectTrigger>
                    <SelectContent>
                      {plants.map((p: any) => (
                        <SelectItem key={p._id} value={p._id}>{p.code} — {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Target Product *</label>
                  <Select value={formData.targetProductId} onValueChange={v => setFormData({...formData, targetProductId: v})}>
                    <SelectTrigger><SelectValue placeholder="Select product…" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p: any) => (
                        <SelectItem key={p._id} value={p._id}>{p.code} — {p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Target Qty *</label>
                  <Input type="number" value={formData.targetQty} onChange={e => setFormData({...formData, targetQty: Number(e.target.value)})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unit</label>
                  <Input value={formData.targetUnit} onChange={e => setFormData({...formData, targetUnit: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Source Tank</label>
                  <Select value={formData.sourceTankId} onValueChange={v => setFormData({...formData, sourceTankId: v})}>
                    <SelectTrigger><SelectValue placeholder={formData.plantId ? 'Select tank…' : 'Select a plant first'} /></SelectTrigger>
                    <SelectContent>
                      {tanks.map((t: any) => (
                        <SelectItem key={t._id} value={t._id}>{t.code} — {t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scheduled Start</label>
                  <Input type="date" value={formData.scheduledStart} onChange={e => setFormData({...formData, scheduledStart: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Scheduled End</label>
                  <Input type="date" value={formData.scheduledEnd} onChange={e => setFormData({...formData, scheduledEnd: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="released">Released</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Costing Method</label>
                  <Select value={formData.costingMethod} onValueChange={v => setFormData({...formData, costingMethod: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="actual">Actual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Joint Cost Method</label>
                  <Select value={formData.jointCostMethod} onValueChange={v => setFormData({...formData, jointCostMethod: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nrv">NRV</SelectItem>
                      <SelectItem value="sales_value_split_off">Sales Value at Split-Off</SelectItem>
                      <SelectItem value="physical_quantity">Physical Quantity</SelectItem>
                      <SelectItem value="weighted_factor">Weighted Factor</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">{editingOrder ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reserve Materials Dialog — lines come from the order's formula BOM,
          scaled to targetQty, so the accountant only has to pick a tank per
          ingredient instead of typing productIds and quantities by hand. */}
      <Dialog open={!!reservingOrder} onOpenChange={(open) => !open && setReservingOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reserve Materials — {reservingOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReserveSubmit}>
            <div className="p-4 space-y-3">
              {reserveLines.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {reservingOrder?.formulaId ? 'Loading formula ingredients…' : 'This order has no formula set, so there are no ingredient lines to reserve.'}
                </p>
              ) : (
                reserveLines.map((line, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_110px_1fr] gap-3 items-end border-b pb-3 last:border-b-0">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Ingredient</label>
                      <p className="text-sm font-medium">{line.productName}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Qty ({line.unit})</label>
                      <Input
                        type="number"
                        value={line.reservedQty}
                        onChange={(e) => setReserveLines((prev) => prev.map((l, i) => (i === idx ? { ...l, reservedQty: Number(e.target.value) } : l)))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Source Tank *</label>
                      <Select value={line.tankId} onValueChange={(v) => setReserveLines((prev) => prev.map((l, i) => (i === idx ? { ...l, tankId: v } : l)))}>
                        <SelectTrigger><SelectValue placeholder="Select tank…" /></SelectTrigger>
                        <SelectContent>
                          {reserveTanks.map((t: any) => (
                            <SelectItem key={t._id} value={t._id}>{t.code} — {t.name} ({t.currentMass ?? 0} {line.unit} on hand)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReservingOrder(null)}>Cancel</Button>
              <Button type="submit" disabled={reserveSaving || reserveLines.length === 0}>
                {reserveSaving ? 'Reserving…' : 'Reserve Materials'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}